import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useParams, useLocation } from 'react-router-dom';
import { auth, db } from './firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot, collection, query, where } from 'firebase/firestore';
import Chat from './chat.js';
import Auth from './Auth.js';
import MyProfile from './MyProfile.js';
import TalentCardView from './TalentCardView.js';
import Welcome from './Welcome.js';
import ActivateAgent from './ActivateAgent.js';
import Marketing from './Marketing.js';
import MainLayout from './MainLayout.js';
import TipDashboard from './TipDashboard.js';
import AdminDashboard from './AdminDashboard.js';
import SalesDashboard from './SalesDashboard.js';
import TeamMemberTaskPage from './TeamMemberTaskPage.js';
import HmTaskPage from './HmTaskPage';
import CandidateInterviews from './CandidateInterviews';
import SharedTalentPool from './SharedTalentPool';
import HRDashboard from './HRDashboard';
import InviteEmployees from './InviteEmployees';
import OrganizationOnboarding from './OrganizationOnboarding';
import OrganizationSettings from './components/OrganizationSettings';
import DigitalTwinChat from './DigitalTwinChat.js';
import AgentPerformance from './AgentPerformance.js';
import About from './About.js';
import HelpCenter from './HelpCenter.js';
import Accessibility from './Accessibility.js';
import PrivacyTerms from './PrivacyTerms.js';
import Logo from './components/Logo';
import PublicLayout from './PublicLayout.js';
import './App.css';

// New imports for the hiring process
import HiringProcess from './pages/hiring/HiringProcess';
import Page1_Landing from './pages/hiring/Page1_Landing';
import Page2_JobDescription from './pages/hiring/Page2_JobDescription';
import Page3_TeamMembers from './pages/hiring/Page3_TeamMembers';
import Page4_Launch from './pages/hiring/Page4_Launch';

const ProtectedRoute = ({ user, userProfile, children, allowedRoles }) => {
  const location = useLocation();
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (userProfile && userProfile.onboardingComplete === false && location.pathname !== '/profile') {
    return <Navigate to="/profile" replace />;
  }
  
  if (allowedRoles && userProfile) {
     const userRoles = userProfile.roles || [];
     const hasPermission = allowedRoles.some(role => userRoles.includes(role));
     if (!hasPermission) {
       return <Navigate to="/welcome" replace />;
     }
  }

  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [teamMemberJds, setTeamMemberJds] = useState([]);
  const [createdJds, setCreatedJds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser && currentUser.emailVerified) {
        setUser(currentUser);
        const userDocRef = doc(db, 'users', currentUser.uid);
        onSnapshot(userDocRef, (doc) => {
          if (doc.exists()) {
            const userData = doc.data();
            setUserProfile({ 
              ...userData.profile, 
              roles: userData.profile?.roles || userData.roles || [],
              status: userData.status 
            });
          }
          setLoading(false);
        });
      } else {
        setUser(null);
        setUserProfile(null);
        setTeamMemberJds([]);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      const q = query(collection(db, 'jobDescriptions'), where('teamMemberUids', 'array-contains', user.uid));
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const jds = [];
        querySnapshot.forEach((doc) => {
          jds.push({ id: doc.id, ...doc.data() });
        });
        setTeamMemberJds(jds);
      });
      return () => unsubscribe();
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      const q = query(collection(db, 'jobDescriptions'), where('createdBy', '==', user.uid));
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const jds = [];
        querySnapshot.forEach((doc) => {
          jds.push({ id: doc.id, ...doc.data() });
        });
        setCreatedJds(jds);
      });
      return () => unsubscribe();
    }
  }, [user]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100dvh' }}>
        <Logo size={48} className="loading-logo" />
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <Routes>
          <Route path="/" element={user ? <Navigate to="/welcome" replace /> : <Navigate to="/login" replace />} />
          
          {/* Public Routes */}
          <Route element={<PublicLayout />}>
            <Route path="/about" element={<About />} />
            <Route path="/help-center" element={<HelpCenter />} />
            <Route path="/accessibility" element={<Accessibility />} />
            <Route path="/privacy-terms" element={<PrivacyTerms />} />
          </Route>

          <Route element={<MainLayout user={user} userProfile={userProfile} createdJds={createdJds} />}>
            <Route path="/welcome" element={<ProtectedRoute user={user} userProfile={userProfile}><Welcome user={user} userProfile={userProfile} teamMemberJds={teamMemberJds} createdJds={createdJds} /></ProtectedRoute>} />
            <Route path="/profile" element={user ? <MyProfile userProfile={userProfile} /> : <Navigate to="/login" replace />} />
            <Route path="/simulations" element={<ProtectedRoute user={user} userProfile={userProfile}><ActivateAgent /></ProtectedRoute>} />
            <Route path="/agent-performance" element={<ProtectedRoute user={user} userProfile={userProfile}><AgentPerformance /></ProtectedRoute>} />
            
            {/* New Hiring Process Routes */}
            <Route path="/start-hiring" element={<ProtectedRoute user={user} userProfile={userProfile} allowedRoles={['admin', 'hr']}><HiringProcess /></ProtectedRoute>}>
              <Route index element={<Page1_Landing />} />
              <Route path="description" element={<Page2_JobDescription />} />
              <Route path="team" element={<Page3_TeamMembers />} />
              <Route path="launch" element={<Page4_Launch />} />
            </Route>


            <Route path="/admin" element={<ProtectedRoute user={user} userProfile={userProfile} allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />
            <Route path="/organization-settings/:orgId" element={<ProtectedRoute user={user} userProfile={userProfile} allowedRoles={['admin', 'sales']}><OrganizationSettings /></ProtectedRoute>} />
            <Route path="/sales-dashboard" element={<ProtectedRoute user={user} userProfile={userProfile} allowedRoles={['admin', 'sales']}><SalesDashboard /></ProtectedRoute>} />
            <Route path="/team-member-tasks/:jdId" element={<ProtectedRoute user={user} userProfile={userProfile}><TeamMemberTaskPage /></ProtectedRoute>} />
            <Route path="/hm-tasks/:jdId" element={<ProtectedRoute user={user} userProfile={userProfile} allowedRoles={['admin', 'hm']}><HmTaskPage /></ProtectedRoute>} />
            <Route path="/candidate-interviews" element={<ProtectedRoute user={user} userProfile={userProfile}><CandidateInterviews /></ProtectedRoute>} />
            <Route path="/talent-pool/:orgId" element={<ProtectedRoute user={user} userProfile={userProfile} allowedRoles={['admin', 'hr']}><SharedTalentPool /></ProtectedRoute>} />
            <Route path="/talent-pool" element={<ProtectedRoute user={user} userProfile={userProfile} allowedRoles={['admin', 'hr']}><SharedTalentPool /></ProtectedRoute>} />
            <Route path="/hr-dashboard" element={<ProtectedRoute user={user} userProfile={userProfile} allowedRoles={['admin', 'hr']}><HRDashboard /></ProtectedRoute>} />
            <Route path="/invite-employees" element={<ProtectedRoute user={user} userProfile={userProfile} allowedRoles={['admin', 'hr']}><InviteEmployees /></ProtectedRoute>} />
            <Route path="/organization-onboarding" element={<ProtectedRoute user={user} userProfile={userProfile}><OrganizationOnboarding /></ProtectedRoute>} />
          </Route>

          <Route path="/chat" element={<ProtectedRoute user={user} userProfile={userProfile}><Chat user={user} /></ProtectedRoute>} />
          <Route path="/chat/:sessionId" element={<ProtectedRoute user={user} userProfile={userProfile}><ChatWrapper /></ProtectedRoute>} />
          <Route path="/login" element={user ? <Navigate to="/welcome" replace /> : <Auth setUser={setUser} />} />
          <Route path="/talent-card/:id" element={<TalentCardView />} />
          <Route path="/product" element={<Marketing />} />
          <Route path="/digital-twin/:vtcId" element={<DigitalTwinChat />} />
        </Routes>
      </div>
    </Router>
  );
}

// A wrapper component to pass the sessionId from URL params to the Chat component
const ChatWrapper = () => {
  const { sessionId } = useParams();
  return <Chat user={auth.currentUser} initialSessionId={sessionId} />;
};

export default App;
